import React from 'react';

import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useRegisterLoader } from '@liscio/common';

import {
  useHomeData,
  useTermsAndConditions,
} from 'fetch-utils/users/user-hooks';
import { LOGOUT_REDIRECT_URL } from 'modules/authenticate/route-constants';
import { POST_TERMS_DASHBOARD_PATH } from 'modules/dashboard/postTermsDashboard';
import { PROFILE_DOMAIN, PROFILE_PATHS } from 'modules/profile/route-constants';
import { safeInternalRedirect } from 'modules/profile/safeInternalRedirect';

export type AcceptedTermsAndConditionsRouteProps = {};

/**
 * AcceptedTermsAndConditionsRoute allows for inspecting terms & conditions acceptance state
 * and redirecting as necessary.
 */
export const AcceptedTermsAndConditionsRoute: React.FC<
  AcceptedTermsAndConditionsRouteProps
> = () => {
  const location = useLocation();
  const { data: homeData, isLoading: isLoadingHomeData } = useHomeData();
  const hasAgreedToTerm = homeData?.data?.has_agreed_to_term?.has_agreed;
  const acceptedTermVersion =
    homeData?.data?.has_agreed_to_term?.agreed_terms_version;

  const {
    data: termsAndConditions,
    isLoading: isLoadingTermsAndConditions,
    error,
  } = useTermsAndConditions();
  const hasTermsAndConditionsLoadingError = Boolean(error);

  // Register loading state with unified loader
  useRegisterLoader('terms-and-conditions', isLoadingHomeData);

  if (isLoadingHomeData) {
    return null; // Loader is handled by unified loader
  }

  // The user is using the app and has not accepted T&C
  if (
    (hasAgreedToTerm === false ||
      (!isLoadingTermsAndConditions &&
        !hasTermsAndConditionsLoadingError &&
        termsAndConditions?.data?.term?.version_number !== acceptedTermVersion)) &&
    location.pathname !==
      `/${PROFILE_DOMAIN}${PROFILE_PATHS.termsAndConditionsAcceptance}`
  ) {
    return (
      <Navigate
        to={`/${PROFILE_DOMAIN}${PROFILE_PATHS.termsAndConditionsAcceptance}`}
        replace
      />
    );
  }

  // The user has accepted T&C and is using the accept T&C page
  if (
    hasAgreedToTerm &&
    !isLoadingTermsAndConditions &&
    !hasTermsAndConditionsLoadingError &&
    termsAndConditions?.data?.term?.version_number === acceptedTermVersion &&
    location.pathname ===
      `/${PROFILE_DOMAIN}${PROFILE_PATHS.termsAndConditionsAcceptance}`
  ) {
    const redirect = new URLSearchParams(location.search).get('redirect');
    return (
      <Navigate
        to={safeInternalRedirect(redirect) || POST_TERMS_DASHBOARD_PATH}
        replace
      />
    );
  }

  // If we can't load the terms and conditions we should logout the user
  if (hasTermsAndConditionsLoadingError) {
    return <Navigate to={LOGOUT_REDIRECT_URL} />;
  }

  // Render Route
  return <Outlet />;
};

export default AcceptedTermsAndConditionsRoute;
